import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import CameraFileInput from '../components/CameraFileInput';
import { FileText, UtensilsCrossed, Pill, ActivitySquare, QrCode, Loader2, Camera, FlaskConical } from 'lucide-react';
import { appTodayDate, toAppDateInputValue, toAppTimeInputValue } from '../lib/appDateTime';

const parseProtocolHours = (protocol) => {
  const startHour = parseInt((protocol.start_time || '08:00').split(':')[0], 10) || 8;
  if (['specific_times', 'times_per_day'].includes(protocol.frequency_mode) && protocol.specific_times?.length > 0) {
    return protocol.specific_times
      .map((item) => parseInt(String(item).split(':')[0], 10))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b);
  }
  if (protocol.frequency_mode === 'sos' || String(protocol.frequency || '').toLowerCase().includes('sos')) {
    return [];
  }
  if (protocol.frequency_mode === 'interval_hours' && protocol.frequency_value) {
    const interval = Number(protocol.frequency_value);
    return Array.from({ length: Math.max(1, Math.floor(24 / interval)) }, (_, index) => (startHour + index * interval) % 24).sort((a, b) => a - b);
  }
  if (protocol.frequency_mode === 'times_per_day' && protocol.frequency_value) {
    const times = Number(protocol.frequency_value);
    const interval = Math.max(1, Math.floor(24 / times));
    return Array.from({ length: times }, (_, index) => (startHour + index * interval) % 24).sort((a, b) => a - b);
  }
  return [startHour];
};

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;
const getTodayDate = () => appTodayDate();
const canSetProtocolExecutionDateTime = (user) => ['veterinario', 'administrador', 'programador'].includes(user?.role);
const PROTOCOL_CONFIRMATION_KEYWORD = 'CONFIRMAR';

const toDateInputValue = (dateValue = new Date()) => {
  return toAppDateInputValue(dateValue);
};

const toTimeInputValue = (dateValue = new Date()) => {
  return toAppTimeInputValue(dateValue);
};

const getFeedingStatusLabel = (status) => {
  switch (status) {
    case 'done':
      return 'Registrado';
    case 'partial':
      return 'Parcial';
    case 'refused':
      return 'Recusada';
    case 'blocked':
      return 'Bloqueado';
    case 'overdue':
      return 'Atrasado';
    case 'upcoming':
      return 'Próximo';
    default:
      return 'Pendente';
  }
};

const getFeedingStatusClasses = (status) => {
  switch (status) {
    case 'done':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'partial':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'refused':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'blocked':
      return 'bg-slate-200 text-slate-700 border-slate-300';
    case 'overdue':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'upcoming':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

const animalHasOutcome = (animal) => {
  if (!animal) return false;
  const inactiveStatuses = ['released', 'death', 'transfer_external', 'escape', 'finalizado', 'Liberado', 'Óbito', 'Eutanásia', 'Alta'];
  return animal.is_active === false
    || Boolean(animal.outcome_date || animal.outcome)
    || inactiveStatuses.includes(animal.status);
};

const protocolNeedsRetroactiveExecutionConfirmation = (protocol, executionDateValue) => {
  if (!protocol) return false;
  if (protocol.status === 'cancelled' || protocol.cancelled_at) return true;
  if (protocol.status === 'suspended' || protocol.suspended_at || protocol.active === false) return true;
  const executionDay = executionDateValue || toDateInputValue();
  const endDay = protocol.end_date ? toDateInputValue(protocol.end_date) : '';
  return Boolean(endDay && executionDay > endDay);
};

const protocolStatusLabel = (protocol) => {
  if (protocol.status === 'cancelled' || protocol.cancelled_at) return 'Cancelado';
  if (protocol.status === 'suspended' || protocol.suspended_at || protocol.active === false) return 'Suspenso';
  return 'Ativo';
};

const getMedicationStatusLabel = (status) => {
  switch (status) {
    case 'done':
      return 'Executado';
    case 'upcoming':
      return 'Próximo';
    case 'overdue':
      return 'Atrasado';
    default:
      return 'Pendente';
  }
};

export default function QuickAnimalActionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [animal, setAnimal] = useState(null);
  const [feedingPlan, setFeedingPlan] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingFeeding, setSavingFeeding] = useState(false);
  const [savingMedication, setSavingMedication] = useState(false);
  const [selectedAction, setSelectedAction] = useState(searchParams.get('action') || 'menu');
  const [feedingDate, setFeedingDate] = useState(getTodayDate());
  const [feedingScheduleEntries, setFeedingScheduleEntries] = useState([]);

  const [feedingHour, setFeedingHour] = useState('');
  const [feedingStatus, setFeedingStatus] = useState('Aceita');
  const [feedingAdministrationType, setFeedingAdministrationType] = useState('Voluntária');
  const [feedingNotes, setFeedingNotes] = useState('');
  const [feedingMediaFile, setFeedingMediaFile] = useState(null);

  const [selectedProtocolId, setSelectedProtocolId] = useState('');
  const [selectedMedicationHour, setSelectedMedicationHour] = useState('');
  const [medicationStatus, setMedicationStatus] = useState('realizado');
  const [medicationNotes, setMedicationNotes] = useState('');
  const [medicationExecutionDate, setMedicationExecutionDate] = useState(() => toDateInputValue());
  const [medicationExecutionTime, setMedicationExecutionTime] = useState(() => toTimeInputValue());
  const [medicationMediaFile, setMedicationMediaFile] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [animalRes, planRes, protocolsRes] = await Promise.all([
          axios.get(`${API}/admissions/${id}`),
          axios.get(`${API}/animals/${id}/feeding-plan`).catch(() => ({ data: null })),
          axios.get(`${API}/animals/${id}/medication-protocols`).catch(() => ({ data: [] }))
        ]);
        setAnimal(animalRes.data);
        setFeedingPlan(planRes.data);
        setProtocols(protocolsRes.data || []);
      } catch (error) {
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id, navigate]);

  useEffect(() => {
    const requestedAction = searchParams.get('action');
    if (requestedAction) {
      setSelectedAction(requestedAction);
    }
  }, [searchParams]);

  const protocolOptions = useMemo(() => protocols.map((protocol) => ({
    ...protocol,
    hours: parseProtocolHours(protocol)
  })), [protocols]);

  useEffect(() => {
    const loadFeedingSchedule = async () => {
      if (!animal?.base_id || !feedingPlan) {
        setFeedingScheduleEntries([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/feeding-dashboard`, {
          params: {
            base_id: animal.base_id,
            date: feedingDate
          }
        });
        const entry = (response.data.grouped_schedules || []).find((item) => item.animal_id === id);
        setFeedingScheduleEntries(entry?.schedules || []);
      } catch (error) {
        setFeedingScheduleEntries([]);
      }
    };
    loadFeedingSchedule();
  }, [animal, feedingPlan, feedingDate, id]);

  useEffect(() => {
    const firstAvailable = feedingScheduleEntries.find((entry) => !entry.blocked);
    if (firstAvailable && !feedingScheduleEntries.some((entry) => String(entry.hour) === feedingHour)) {
      setFeedingHour(String(firstAvailable.hour));
      return;
    }
    if (!feedingHour && firstAvailable) {
      setFeedingHour(String(firstAvailable.hour));
    }
  }, [feedingScheduleEntries, feedingHour]);

  useEffect(() => {
    if (protocolOptions.length > 0 && !selectedProtocolId) {
      setSelectedProtocolId(protocolOptions[0].id);
    }
  }, [protocolOptions, selectedProtocolId]);

  useEffect(() => {
    const protocol = protocolOptions.find((item) => item.id === selectedProtocolId);
    if (protocol && protocol.hours.length > 0) {
      setSelectedMedicationHour(String(protocol.hours[0]));
    } else if (protocol) {
      setSelectedMedicationHour('');
    }
  }, [selectedProtocolId, protocolOptions]);

  const handleFeedingSubmit = async () => {
    const selectedFeedingEntry = feedingScheduleEntries.find((entry) => String(entry.hour) === feedingHour);
    if (!feedingPlan || feedingHour === '') {
      toast.error('Selecione um horário de alimentação');
      return;
    }
    if (selectedFeedingEntry?.blocked) {
      toast.error('Este horário está bloqueado por jejum');
      return;
    }
    setSavingFeeding(true);
    try {
      const response = await axios.post(`${API}/feeding-dashboard/quick-record?animal_id=${id}&hour=${feedingHour}`, null, {
        params: {
          status: feedingStatus,
          administration_type: feedingAdministrationType,
          notes: feedingNotes || null,
          date: feedingDate
        }
      });
      let uploadedPhoto = false;
      let photoUploadFailed = false;
      if (feedingMediaFile && response.data?.record_id) {
        const formData = new FormData();
        formData.append('file', feedingMediaFile);
        formData.append('caption', 'Foto do alimento');
        try {
          await axios.post(`${API}/feeding-records/${response.data.record_id}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          uploadedPhoto = true;
        } catch (uploadError) {
          photoUploadFailed = true;
          console.error('Error uploading feeding photo:', uploadError);
        }
      }
      if (photoUploadFailed) {
        toast.error('Alimentação registrada pelo QR, mas a foto não foi enviada');
      } else {
        toast.success(uploadedPhoto ? 'Alimentação registrada pelo QR com foto!' : 'Alimentação registrada pelo QR');
      }
      setFeedingNotes('');
      setFeedingMediaFile(null);
      const refresh = await axios.get(`${API}/feeding-dashboard`, {
        params: {
          base_id: animal.base_id,
          date: feedingDate
        }
      });
      const entry = (refresh.data.grouped_schedules || []).find((item) => item.animal_id === id);
      setFeedingScheduleEntries(entry?.schedules || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar alimentação');
    } finally {
      setSavingFeeding(false);
    }
  };

  const handleMedicationSubmit = async () => {
    if (!selectedProtocolId) {
      toast.error('Selecione um protocolo');
      return;
    }
    const selectedProtocol = protocolOptions.find((item) => item.id === selectedProtocolId);
    const requiresConfirmation = animalHasOutcome(animal)
      || protocolNeedsRetroactiveExecutionConfirmation(selectedProtocol, medicationExecutionDate);
    let protocolConfirmation = null;
    if (requiresConfirmation) {
      const typed = window.prompt(`Este registro exige confirmação. Digite ${PROTOCOL_CONFIRMATION_KEYWORD} para continuar.`);
      if (typed === null) return;
      if (typed.trim().toUpperCase() !== PROTOCOL_CONFIRMATION_KEYWORD) {
        toast.error(`Confirmação inválida. Digite ${PROTOCOL_CONFIRMATION_KEYWORD} para continuar.`);
        return;
      }
      protocolConfirmation = PROTOCOL_CONFIRMATION_KEYWORD;
    }
    setSavingMedication(true);
    try {
      const response = await axios.post(`${API}/protocols/${selectedProtocolId}/execute?protocol_type=medication`, {
        status: medicationStatus,
        scheduled_hour: selectedMedicationHour === '' ? null : parseInt(selectedMedicationHour, 10),
        notes: medicationNotes || null,
        ...(protocolConfirmation ? { outcome_override_confirmation: protocolConfirmation } : {}),
        ...(canSetProtocolExecutionDateTime(user) ? {
          execution_date: medicationExecutionDate || null,
          execution_time: medicationExecutionTime || null
        } : {})
      });
      let uploadedMedia = false;
      let mediaUploadFailed = false;
      if (medicationMediaFile && response.data?.id) {
        const formData = new FormData();
        formData.append('file', medicationMediaFile);
        formData.append('caption', medicationMediaFile.name);
        try {
          await axios.post(`${API}/protocol-executions/${response.data.id}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedMedia = true;
        } catch (uploadError) {
          mediaUploadFailed = true;
          console.error('Error uploading protocol execution media:', uploadError);
        }
      }
      if (mediaUploadFailed) {
        toast.error('Medicação registrada pelo QR, mas a foto não foi enviada');
      } else {
        toast.success(uploadedMedia ? 'Medicação registrada pelo QR com foto!' : 'Medicação registrada pelo QR');
      }
      setMedicationNotes('');
      setMedicationExecutionDate(toDateInputValue());
      setMedicationExecutionTime(toTimeInputValue());
      setMedicationMediaFile(null);
      const protocolsRes = await axios.get(`${API}/animals/${id}/medication-protocols`).catch(() => ({ data: [] }));
      setProtocols(protocolsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar medicação');
    } finally {
      setSavingMedication(false);
    }
  };

  const selectedProtocol = protocolOptions.find((item) => item.id === selectedProtocolId);
  const selectedFeedingEntry = feedingScheduleEntries.find((entry) => String(entry.hour) === feedingHour);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="text-center text-emerald-800 flex items-center justify-center gap-2">
              <QrCode className="w-5 h-5" />
              Ações rápidas por QR Code
            </CardTitle>
            <p className="text-center text-sm text-gray-500">
              {animal ? `${animal.ibama_id} - ${animal.common_name} - ${animal.allocation_location}/${animal.bay_number}` : 'Carregando animal...'}
            </p>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-4">
            <Button variant={selectedAction === 'menu' ? 'default' : 'outline'} className="h-auto py-4" onClick={() => setSelectedAction('menu')}>
              <FileText className="w-5 h-5 mr-2" />
              Menu
            </Button>
            <Button variant={selectedAction === 'feeding' ? 'default' : 'outline'} className="h-auto py-4" onClick={() => setSelectedAction('feeding')}>
              <UtensilsCrossed className="w-5 h-5 mr-2" />
              Alimentação
            </Button>
            <Button variant={selectedAction === 'medication' ? 'default' : 'outline'} className="h-auto py-4" onClick={() => setSelectedAction('medication')}>
              <Pill className="w-5 h-5 mr-2" />
              Medicação
            </Button>
            <Button variant={selectedAction === 'monitoring' ? 'default' : 'outline'} className="h-auto py-4" onClick={() => setSelectedAction('monitoring')}>
              <ActivitySquare className="w-5 h-5 mr-2" />
              Acompanhamento
            </Button>
          </CardContent>
        </Card>

        {selectedAction === 'menu' && (
          <Card>
            <CardContent className="grid gap-3 pt-6 md:grid-cols-2">
              <Button className="h-auto py-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/animals/${id}/record`)}>
                <FileText className="w-5 h-5 mr-2" />
                Abrir prontuário completo
              </Button>
              <Button className="h-auto py-4 bg-amber-600 hover:bg-amber-700" onClick={() => navigate(`/animals/${id}/record?tab=feeding`)}>
                <UtensilsCrossed className="w-5 h-5 mr-2" />
                Abrir aba de alimentação
              </Button>
              <Button className="h-auto py-4 bg-orange-600 hover:bg-orange-700" onClick={() => navigate(`/animals/${id}/record?tab=protocols`)}>
                <Pill className="w-5 h-5 mr-2" />
                Abrir aba de protocolos
              </Button>
              <Button className="h-auto py-4 bg-sky-600 hover:bg-sky-700" onClick={() => navigate(`/animals/${id}/record?tab=monitoring`)}>
                <ActivitySquare className="w-5 h-5 mr-2" />
                Abrir aba de acompanhamento
              </Button>
              <Button className="h-auto py-4 bg-cyan-600 hover:bg-cyan-700" onClick={() => navigate(`/animals/${id}/record?tab=exams`)}>
                <FlaskConical className="w-5 h-5 mr-2" />
                Abrir aba de exames
              </Button>
              <Button className="h-auto py-4 bg-slate-700 hover:bg-slate-800" onClick={() => navigate('/qr-scanner')}>
                <Camera className="w-5 h-5 mr-2" />
                Ler outro QR
              </Button>
            </CardContent>
          </Card>
        )}

        {selectedAction === 'feeding' && (
          <Card>
            <CardHeader><CardTitle className="text-emerald-800">Registro rápido de alimentação</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!feedingPlan ? (
                <p className="text-sm text-gray-500">Este animal não possui plano alimentar ativo.</p>
              ) : (
                <>
                  <div className="rounded bg-emerald-50 p-3 text-sm">
                    <p><strong>Plano:</strong> {feedingPlan.food_type} - {feedingPlan.quantity} {feedingPlan.unit} - {feedingPlan.form}</p>
                  </div>
                  <div>
                    <Label>Data programada</Label>
                    <Input type="date" value={feedingDate} onChange={(e) => setFeedingDate(e.target.value)} />
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label>Horário</Label>
                      <Select value={feedingHour} onValueChange={setFeedingHour}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {feedingScheduleEntries.map((entry) => {
                            const hourValue = String(entry.hour);
                            return (
                              <SelectItem key={entry.hour} value={hourValue}>
                                {formatHour(entry.hour)} - {getFeedingStatusLabel(entry.status)}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={feedingStatus} onValueChange={setFeedingStatus}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Aceita">Aceita</SelectItem>
                          <SelectItem value="Parcial">Parcial</SelectItem>
                          <SelectItem value="Recusada">Recusada</SelectItem>
                          <SelectItem value="Oferecida">Oferecida</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo de administração</Label>
                      <Select value={feedingAdministrationType} onValueChange={setFeedingAdministrationType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Voluntária">Voluntária</SelectItem>
                          <SelectItem value="Forçada">Forçada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {feedingScheduleEntries.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {feedingScheduleEntries.map((entry) => (
                        <button
                          key={entry.hour}
                          type="button"
                          onClick={() => !entry.blocked && setFeedingHour(String(entry.hour))}
                          disabled={entry.blocked}
                          className={`rounded border px-3 py-2 text-sm ${getFeedingStatusClasses(entry.status)} ${entry.blocked ? 'cursor-not-allowed opacity-70' : 'hover:opacity-90'}`}
                        >
                          <div className="font-semibold">{formatHour(entry.hour)}</div>
                          <div>{getFeedingStatusLabel(entry.status)}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedFeedingEntry?.blocked && (
                    <div className="rounded border border-slate-300 bg-slate-100 p-3 text-sm text-slate-700">
                      <p><strong>Horário bloqueado por jejum.</strong></p>
                      <p>Procedimento: {selectedFeedingEntry.blocked_procedure || 'Não informado'}</p>
                      <p>Motivo: {selectedFeedingEntry.blocked_reason || 'Não informado'}</p>
                    </div>
                  )}
                  <div>
                    <Label>Observações</Label>
                    <Textarea value={feedingNotes} onChange={(e) => setFeedingNotes(e.target.value)} rows={3} />
                  </div>
                  <div>
                    <Label>Foto do alimento (opcional)</Label>
                    <CameraFileInput
                      accept="image/*"
                      disabled={savingFeeding}
                      onFileSelected={setFeedingMediaFile}
                      fileLabel="Selecionar foto"
                      cameraLabel="Tirar foto"
                      data-testid="quick-feeding-photo-input"
                    />
                    {feedingMediaFile && (
                      <p className="mt-1 text-xs text-gray-500">Selecionado: {feedingMediaFile.name}</p>
                    )}
                  </div>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleFeedingSubmit} disabled={savingFeeding || selectedFeedingEntry?.blocked}>
                    {savingFeeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Registrar alimentação
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {selectedAction === 'medication' && (
          <Card>
            <CardHeader><CardTitle className="text-emerald-800">Registro rápido de medicação</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {protocolOptions.length === 0 ? (
                <p className="text-sm text-gray-500">Este animal não possui protocolos medicamentosos ativos.</p>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Protocolo</Label>
                      <Select value={selectedProtocolId} onValueChange={setSelectedProtocolId}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {protocolOptions.map((protocol) => (
                            <SelectItem key={protocol.id} value={protocol.id}>
                              {protocol.medication_name} - {protocol.frequency} - {protocolStatusLabel(protocol)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Horário programado</Label>
                      <Select value={selectedMedicationHour} onValueChange={setSelectedMedicationHour}>
                        <SelectTrigger><SelectValue placeholder={selectedProtocol?.hours?.length ? 'Selecione' : 'SOS'} /></SelectTrigger>
                        <SelectContent>
                          {(selectedProtocol?.hours || []).map((hour) => (
                            <SelectItem key={hour} value={String(hour)}>{formatHour(hour)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="rounded bg-emerald-50 p-3 text-sm">
                    <p><strong>Medicamento:</strong> {selectedProtocol?.medication_name || 'N/A'}</p>
                    <p><strong>Dosagem:</strong> {selectedProtocol?.dosage || 'N/A'} | <strong>Via:</strong> {selectedProtocol?.route || 'N/A'}</p>
                    <p><strong>Frequência:</strong> {selectedProtocol?.frequency || 'N/A'}</p>
                  </div>
                  {(selectedProtocol?.hours || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {(selectedProtocol?.hours || []).map((hour) => {
                        const slotStatus = selectedProtocol?.hour_statuses?.[hour] || 'pending';
                        return (
                          <button
                            key={hour}
                            type="button"
                            onClick={() => setSelectedMedicationHour(String(hour))}
                            className={`rounded border px-3 py-2 text-sm ${selectedMedicationHour === String(hour) ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600'}`}
                          >
                            <div className="font-semibold">{formatHour(hour)}</div>
                            <div>{getMedicationStatusLabel(slotStatus)}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {canSetProtocolExecutionDateTime(user) && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>Data executada</Label>
                        <Input
                          type="date"
                          value={medicationExecutionDate}
                          onChange={(e) => setMedicationExecutionDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Hora executada</Label>
                        <Input
                          type="time"
                          value={medicationExecutionTime}
                          onChange={(e) => setMedicationExecutionTime(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Status</Label>
                      <Select value={medicationStatus} onValueChange={setMedicationStatus}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="realizado">Realizado</SelectItem>
                          <SelectItem value="nao_realizado">Não realizado</SelectItem>
                          <SelectItem value="atrasado">Atrasado</SelectItem>
                          <SelectItem value="suspenso">Suspenso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Observações</Label>
                      <Input value={medicationNotes} onChange={(e) => setMedicationNotes(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label>Foto da execução (opcional)</Label>
                    <CameraFileInput
                      accept="image/*"
                      disabled={savingMedication}
                      onFileSelected={setMedicationMediaFile}
                      fileLabel="Selecionar foto"
                      cameraLabel="Tirar foto"
                      data-testid="quick-medication-photo-input"
                    />
                    {medicationMediaFile && (
                      <p className="mt-1 text-xs text-gray-500">Selecionado: {medicationMediaFile.name}</p>
                    )}
                  </div>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleMedicationSubmit} disabled={savingMedication}>
                    {savingMedication ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Registrar medicação
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {selectedAction === 'monitoring' && (
          <Card>
            <CardHeader><CardTitle className="text-emerald-800">Acompanhamento</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600">Use o botão abaixo para abrir diretamente o acompanhamento diário do animal e registrar evolução clínica, exames e mídia.</p>
              <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => navigate(`/animals/${id}/record?tab=monitoring`)}>
                <ActivitySquare className="w-4 h-4 mr-2" />
                Abrir acompanhamento diário
              </Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => navigate(`/animals/${id}/record?tab=exams`)}>
                <FlaskConical className="w-4 h-4 mr-2" />
                Abrir aba de exames
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
